import React from 'react';

interface JobDetails {
  job_id: string;
  title: string;
  company_name: string;
  company_logo?: string;
  location?: string;
  salary_range?: {
    min: number;
    max: number;
    currency: string;
  };
  job_type?: string; // e.g., "Full-time", "Part-time", "Contract"
  work_mode?: string; // e.g., "Remote", "On-site", "Hybrid"
  experience_level?: string; // e.g., "Entry", "Mid", "Senior"
  overview?: string;
  description?: string;
  posted_at?: string;
}

interface SimilarJob {
  job_id: string;
  title: string;
  company_name: string;
  company_logo?: string;
  location?: string;
}

interface JobDetailsSidebarProps {
  /**
   * Job details to display
   */
  jobDetails: JobDetails;

  /**
   * Similar jobs list
   */
  similarJobs?: SimilarJob[];

  /**
   * Callback when a similar job is clicked
   */
  onSimilarJobClick?: (jobId: string) => void;

  /**
   * Callback to close the sidebar
   */
  onClose?: () => void;
}

/**
 * JobDetailsSidebar Component
 * Right sidebar showing job details and similar jobs
 */
const JobDetailsSidebar: React.FC<JobDetailsSidebarProps> = ({
  jobDetails,
  similarJobs = [],
  onSimilarJobClick,
  onClose,
}) => {
  /**
   * Format salary range
   */
  const formatSalary = (): string => {
    if (!jobDetails.salary_range) return 'Not specified';

    const { min, max, currency } = jobDetails.salary_range;
    const formatNumber = (num: number) => {
      if (num >= 1000) {
        return `${(num / 1000).toFixed(0)}k`;
      }
      return num.toString();
    };

    return `${currency} ${formatNumber(min)} - ${formatNumber(max)}`;
  };

  /**
   * Format posted date
   */
  const formatPostedDate = (): string => {
    if (!jobDetails.posted_at) return '';

    try {
      const postedDate = new Date(jobDetails.posted_at);
      const now = new Date();
      const diffInDays = Math.floor((now.getTime() - postedDate.getTime()) / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) return 'Posted today';
      if (diffInDays === 1) return 'Posted yesterday';
      if (diffInDays < 7) return `Posted ${diffInDays} days ago`;
      if (diffInDays < 30) return `Posted ${Math.floor(diffInDays / 7)} weeks ago`;
      return `Posted ${Math.floor(diffInDays / 30)} months ago`;
    } catch (error) {
      return '';
    }
  };

  return (
    <div className="w-full h-full bg-white overflow-hidden flex flex-col">
      {/* Header with close button */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
        <h3 className="font-manrope font-semibold text-base text-[#1A1A1A]">Job Details</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#F1F3F4] transition-colors"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col p-5 gap-5">
          {/* Company logo and header */}
          <div className="flex flex-col items-center gap-3 pb-5 border-b border-[#E5E7EB]">
          {/* Company logo */}
          {jobDetails.company_logo ? (
            <img
              src={jobDetails.company_logo}
              alt={jobDetails.company_name}
              className="w-20 h-20 rounded-2xl object-cover"
            />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-[#F1F3F4] flex items-center justify-center">
              <span className="font-manrope font-semibold text-2xl text-[#757575]">
                {jobDetails.company_name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Job title */}
          <h2 className="font-manrope font-bold text-xl text-[#1A1A1A] text-center">
            {jobDetails.title}
          </h2>

          {/* Company name */}
          <p className="font-manrope font-medium text-sm text-[#757575] text-center">
            {jobDetails.company_name}
          </p>

          {/* Job badges */}
          <div className="flex flex-row flex-wrap gap-2 justify-center">
            {jobDetails.job_type && (
              <span className="px-3 py-1 bg-[#E3F2FD] text-[#015FA6] font-manrope font-medium text-xs rounded-full">
                {jobDetails.job_type}
              </span>
            )}
            {jobDetails.work_mode && (
              <span className="px-3 py-1 bg-[#F3E5F5] text-[#7B1FA2] font-manrope font-medium text-xs rounded-full">
                {jobDetails.work_mode}
              </span>
            )}
            {jobDetails.experience_level && (
              <span className="px-3 py-1 bg-[#FFF3E0] text-[#E65100] font-manrope font-medium text-xs rounded-full">
                {jobDetails.experience_level}
              </span>
            )}
          </div>
        </div>

        {/* Job details */}
        <div className="flex flex-col gap-4">
          {/* Location */}
          {jobDetails.location && (
            <div className="flex flex-row items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 10C11.1046 10 12 9.10457 12 8C12 6.89543 11.1046 6 10 6C8.89543 6 8 6.89543 8 8C8 9.10457 8.89543 10 10 10Z" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 2C7.87827 2 5.84344 2.84285 4.34315 4.34315C2.84285 5.84344 2 7.87827 2 10C2 11.892 2.402 13.13 3.5 14.5L10 22L16.5 14.5C17.598 13.13 18 11.892 18 10C18 7.87827 17.1571 5.84344 15.6569 4.34315C14.1566 2.84285 12.1217 2 10 2Z" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div className="flex flex-col gap-1">
                <p className="font-manrope font-medium text-sm text-[#1A1A1A]">Location</p>
                <p className="font-manrope font-normal text-sm text-[#757575]">{jobDetails.location}</p>
              </div>
            </div>
          )}

          {/* Salary */}
          <div className="flex flex-row items-start gap-3">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="10" cy="10" r="8" stroke="#757575" strokeWidth="1.5"/>
                <path d="M10 5V10L13 12" stroke="#757575" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <div className="flex flex-col gap-1">
              <p className="font-manrope font-medium text-sm text-[#1A1A1A]">Salary Range</p>
              <p className="font-manrope font-normal text-sm text-[#757575]">{formatSalary()}</p>
            </div>
          </div>

          {/* Posted date */}
          {jobDetails.posted_at && (
            <div className="flex flex-row items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="3" y="4" width="14" height="14" rx="2" stroke="#757575" strokeWidth="1.5"/>
                  <path d="M13 2V6M7 2V6M3 8H17" stroke="#757575" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div className="flex flex-col gap-1">
                <p className="font-manrope font-medium text-sm text-[#1A1A1A]">Posted</p>
                <p className="font-manrope font-normal text-sm text-[#757575]">{formatPostedDate()}</p>
              </div>
            </div>
          )}
        </div>

        {/* Overview */}
        {jobDetails.overview && (
          <div className="flex flex-col gap-2 pt-4 border-t border-[#EDEDED]">
            <h3 className="font-manrope font-semibold text-base text-[#1A1A1A]">Overview</h3>
            <p className="font-manrope font-normal text-sm text-[#757575] leading-relaxed">
              {jobDetails.overview}
            </p>
          </div>
        )}

        {/* Description */}
        {jobDetails.description && (
          <div className="flex flex-col gap-2 pt-4 border-t border-[#EDEDED]">
            <h3 className="font-manrope font-semibold text-base text-[#1A1A1A]">Job Description</h3>
            <p className="font-manrope font-normal text-sm text-[#757575] leading-relaxed whitespace-pre-wrap">
              {jobDetails.description}
            </p>
          </div>
        )}

        {/* Company Info */}
        <div className="flex flex-col gap-2 pt-4 border-t border-[#EDEDED]">
          <h3 className="font-manrope font-semibold text-base text-[#1A1A1A]">About {jobDetails.company_name}</h3>
          <p className="font-manrope font-normal text-sm text-[#757575] leading-relaxed">
            Learn more about the company and their values.
          </p>
          <button className="self-start mt-2 px-4 py-2 bg-[#F1F3F4] text-[#015FA6] font-manrope font-medium text-sm rounded-lg hover:bg-[#E0E0E0] transition-colors">
            View Company Profile
          </button>
        </div>

        {/* Similar Jobs */}
        {similarJobs.length > 0 && (
          <div className="flex flex-col gap-4 pt-4 border-t border-[#EDEDED]">
            <h3 className="font-manrope font-semibold text-base text-[#1A1A1A]">Similar Jobs</h3>
            <div className="flex flex-col gap-3">
              {similarJobs.slice(0, 3).map((job) => (
                <button
                  key={job.job_id}
                  onClick={() => onSimilarJobClick?.(job.job_id)}
                  className="flex flex-row items-center gap-3 p-3 bg-[#F9F9F9] rounded-lg hover:bg-[#F1F3F4] transition-colors text-left"
                >
                  {/* Company logo */}
                  {job.company_logo ? (
                    <img
                      src={job.company_logo}
                      alt={job.company_name}
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#E0E0E0] flex items-center justify-center flex-shrink-0">
                      <span className="font-manrope font-semibold text-sm text-[#757575]">
                        {job.company_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}

                  {/* Job info */}
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <p className="font-manrope font-semibold text-sm text-[#1A1A1A] truncate">
                      {job.title}
                    </p>
                    <p className="font-manrope font-normal text-xs text-[#757575] truncate">
                      {job.company_name}
                    </p>
                    {job.location && (
                      <p className="font-manrope font-normal text-xs text-[#757575] truncate">
                        {job.location}
                      </p>
                    )}
                  </div>

                  {/* Arrow icon */}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="flex-shrink-0">
                    <path d="M7 4L13 10L7 16" stroke="#757575" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              ))}
            </div>

            {similarJobs.length > 3 && (
              <button className="self-start px-4 py-2 bg-[#F1F3F4] text-[#015FA6] font-manrope font-medium text-sm rounded-lg hover:bg-[#E0E0E0] transition-colors">
                View All Similar Jobs
              </button>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default JobDetailsSidebar;
